import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  CheckCircle2,
  Clock,
  FileText,
  Users,
  Trophy,
  TrendingUp,
  Eye,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GradingPage = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  
  const [overview, setOverview] = useState(null);
  const [pendingAttempts, setPendingAttempts] = useState([]);
  const [allAttempts, setAllAttempts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [overviewRes, pendingRes, allRes] = await Promise.all([
        axios.get(`${API}/analytics/overview`, { headers }),
        axios.get(`${API}/grading/pending`, { headers }),
        axios.get(`${API}/grading/all`, { headers })
      ]);
      setOverview(overviewRes.data);
      setPendingAttempts(pendingRes.data);
      setAllAttempts(allRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load grading data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getScoreColor = (percentage) => {
    if (percentage >= 90) return 'bg-green-100 text-green-700';
    if (percentage >= 70) return 'bg-primary/10 text-primary';
    if (percentage >= 50) return 'bg-yellow-100 text-yellow-700';
    return 'bg-red-100 text-red-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-7xl mx-auto px-4 py-8">
          <Skeleton className="h-12 w-64 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-32" />
            ))}
          </div>
          <Skeleton className="h-96" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="font-serif text-3xl md:text-4xl font-medium text-primary mb-2">
            Grading & Analytics
          </h1>
          <p className="text-muted-foreground">
            Review student submissions and track progress
          </p>
        </motion.div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                </div>
                <p className="text-2xl font-serif font-medium text-primary">{overview?.student_count || 0}</p>
                <p className="text-sm text-muted-foreground">Students</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-primary" />
                  </div>
                </div>
                <p className="text-2xl font-serif font-medium text-primary">{overview?.total_attempts || 0}</p>
                <p className="text-sm text-muted-foreground">Submissions</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-secondary rounded-lg flex items-center justify-center">
                    <Trophy className="w-5 h-5 text-primary" />
                  </div>
                </div>
                <p className="text-2xl font-serif font-medium text-primary">{overview?.overall_average || 0}%</p>
                <p className="text-sm text-muted-foreground">Avg Score</p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className={`border-border ${overview?.pending_reviews > 0 ? 'border-accent/50' : ''}`}>
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    overview?.pending_reviews > 0 ? 'bg-accent/10' : 'bg-secondary'
                  }`}>
                    <Clock className={`w-5 h-5 ${overview?.pending_reviews > 0 ? 'text-accent' : 'text-primary'}`} />
                  </div>
                </div>
                <p className={`text-2xl font-serif font-medium ${
                  overview?.pending_reviews > 0 ? 'text-accent' : 'text-primary'
                }`}>
                  {overview?.pending_reviews || 0}
                </p>
                <p className="text-sm text-muted-foreground">Need Review</p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue={pendingAttempts.length > 0 ? "pending" : "all"} className="space-y-6">
          <TabsList>
            <TabsTrigger value="pending" className="gap-2">
              <Clock className="w-4 h-4" />
              Pending Review
              {pendingAttempts.length > 0 && (
                <Badge variant="secondary" className="ml-1 bg-accent/10 text-accent">
                  {pendingAttempts.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all" className="gap-2">
              <FileText className="w-4 h-4" />
              All Submissions
            </TabsTrigger>
          </TabsList>

          {/* Pending Review */}
          <TabsContent value="pending">
            {pendingAttempts.length > 0 ? (
              <div className="space-y-4">
                {pendingAttempts.map((attempt, index) => (
                  <motion.div
                    key={attempt.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="border-border border-l-4 border-l-accent hover:shadow-md transition-shadow">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-accent/10 rounded-full flex items-center justify-center">
                              <span className="text-lg font-semibold text-accent">
                                {attempt.student_name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <h3 className="font-medium text-foreground">{attempt.student_name}</h3>
                              <p className="text-sm text-muted-foreground">{attempt.quiz_title}</p>
                              <p className="text-xs text-muted-foreground">{formatDate(attempt.submitted_at)}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-lg font-semibold text-accent">
                                {Math.round(attempt.percentage)}%
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {attempt.results.filter(r => r.needs_review).length} to review
                              </p>
                            </div>
                            <Button
                              onClick={() => navigate(`/grading/${attempt.id}`)}
                              className="gap-2"
                              data-testid={`review-btn-${index}`}
                            >
                              <Eye className="w-4 h-4" />
                              Review
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            ) : (
              <Card className="border-border">
                <CardContent className="p-12 text-center">
                  <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="font-serif text-xl font-medium text-primary mb-2">All caught up!</h3>
                  <p className="text-muted-foreground">No submissions need review right now.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* All Submissions */}
          <TabsContent value="all">
            {allAttempts.length > 0 ? (
              <Card className="border-border">
                <CardContent className="p-0">
                  <div className="divide-y divide-border">
                    {allAttempts.map((attempt, index) => (
                      <div
                        key={attempt.id}
                        className="flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/grading/${attempt.id}`)}
                        data-testid={`attempt-row-${index}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center">
                            <span className="font-semibold text-primary">
                              {attempt.student_name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{attempt.student_name}</p>
                            <p className="text-sm text-muted-foreground">{attempt.quiz_title}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getScoreColor(attempt.percentage)}`}>
                              {Math.round(attempt.percentage)}%
                            </span>
                          </div>
                          <div className="text-right">
                            <p className="text-sm text-muted-foreground">{formatDate(attempt.submitted_at)}</p>
                            {attempt.needs_review && (
                              <Badge variant="outline" className="text-accent border-accent/50">
                                Needs Review
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-border">
                <CardContent className="p-12 text-center">
                  <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-serif text-xl font-medium text-primary mb-2">No submissions yet</h3>
                  <p className="text-muted-foreground">Assign quizzes to students to see their results here.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default GradingPage;
